import { useEffect, useRef, useState } from 'react';
import { compact } from 'lodash';
import qs from 'qs';

import { Address, TemperatureUnitEnum } from '@pimm/services/lib/sms-tenants';
import { WeatherForecast as OpenMeteoWeatherForecast } from '../types';
import { WeatherForecast, WeatherForecastStore } from '../store/weather-forecast.store';
import moment from 'moment';

const OPENMETEO_BASE_URL = __DEV__ ? 'https://api.open-meteo.com/v1/forecast' : 'https://customer-api.open-meteo.com/v1/forecast';

type GetWeatherForecastResult = {
  data?: WeatherForecast;
  status: 'pending' | 'success' | 'error';
};

export type GetWeatherForecastParams = {
  address: Address;
  siteId: string;
  temperatureUnit?: TemperatureUnitEnum;
};

export const useGetWeatherForecast = (_query?: GetWeatherForecastParams) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const abortControllerRef = useRef<AbortController>();
  const [forecast, setForecast] = useState<GetWeatherForecastResult>({ status: 'pending' });

  // This is the key for the query cache
  // It is a simplified key for the query cache
  // We don't want to include the whole address object in the key because it is too complex
  const queryKey = compact([_query?.siteId, _query?.address?.latitude, _query?.address?.longitude, _query?.temperatureUnit]).join('.');

  useEffect(() => {
    if (!_query?.address?.latitude || !_query?.address?.longitude) return;

    /**
     * Fetches the latest weather forecast data from Open-Meteo API and stores it in local storage.
     * If a previous fetch is in progress, it is aborted before starting a new request.
     * Updates local state to "pending" while the fetch is in progress.
     * On success, caches the response and sets state to "success".
     */
    const fetchWeatherForecast = async () => {
      const { address, siteId, temperatureUnit } = _query;
      // Cancel any previous fetch to avoid race conditions
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this fetch
      abortControllerRef.current = new AbortController();

      // Set status to pending if not already success
      setForecast(_prev => {
        if (_prev.status !== 'success') {
          return { status: 'pending' };
        }
        return _prev;
      });

      try {
        // Only US uses "imperial" for now, the array can be expanded (e.g. 'LR','MM')
        const imperialCountries = ['US']; // Add 'LR', 'MM' as needed
        const countryCode = address.country?.trim().toUpperCase();
        // Unit logic: if US (or future others), use imperial; otherwise metric.
        const isEmperial = countryCode && imperialCountries.includes(countryCode);

        // Build query string for Open-Meteo API
        const queryString = qs.stringify({
          // Only supply API key if not __DEV__
          ...(__DEV__ ? undefined : { apikey: 'GWHvUzpi8g9c5F88' }),
          latitude: address.latitude,
          longitude: address.longitude,
          daily: 'sunrise,sunset,temperature_2m_max,temperature_2m_min',
          hourly: 'temperature_2m,precipitation_probability,weather_code,is_day',
          current: 'temperature_2m,precipitation,precipitation_probability,weather_code,is_day',
          forecast_hours: 12,
          temperature_unit: temperatureUnit === TemperatureUnitEnum.Fahrenheit ? 'fahrenheit' : 'celsius',
          precipitation_unit: isEmperial ? 'inch' : 'mm',
          wind_speed_unit: isEmperial ? 'mph' : 'kmh',
          timezone: 'auto',
        });

        // Fire off API request with abort controller
        const response = await fetch(`${OPENMETEO_BASE_URL}?${queryString}`, {
          method: 'GET',
          signal: abortControllerRef.current.signal,
        }).then(res => res.json() as Promise<OpenMeteoWeatherForecast>);

        // Only update state if fetch wasn't aborted
        if (!abortControllerRef.current.signal.aborted) {
          // Save to disk cache and update React state
          const weatherForecast = await WeatherForecastStore.set(siteId, response, _query.address?.utcOffsetInMinutes);
          setForecast({ data: weatherForecast, status: 'success' });
        }
      } catch (error) {
        // Only update if fetch wasn't aborted (abort throws an error)
        if (!abortControllerRef.current.signal.aborted) {
          setForecast({ status: 'error' });
        }
      }
    };

    /**
     * Attempts to restore the weather forecast from the local WeatherForecastStore for the current site.
     * If a forecast exists and is up-to-date (i.e., its time falls within the current hour),
     * it will be set as the current forecast with status 'success'.
     * Otherwise, triggers a fetch to get new forecast data.
     */
    const restoreWeatherForecast = async () => {
      const weather = await WeatherForecastStore.get(_query?.siteId);

      // If a weather forecast exists, check if it is up-to-date
      if (weather) {
        // Get today's date with the correct timezone offset
        const today = new Date().targetOffset(_query.address?.utcOffsetInMinutes);
        const lastRecordedTime = moment(weather.time).toDate();
        const startOfHour = moment(today).startOf('hour').toDate();

        // If the stored weather forecast is from the current hour, restore it as valid
        if (lastRecordedTime >= startOfHour && lastRecordedTime <= today) {
          setForecast({ data: weather, status: 'success' });
          return;
        }
      }
      // Otherwise, fetch a new forecast
      fetchWeatherForecast();
    };

    // Calculate milliseconds until next hour
    const scheduleNextFetch = () => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const now = new Date();
      const nextHour = new Date(now);
      nextHour.setHours(now.getHours() + 1, 0, 0, 0); // Set to next hour, 0 minutes, 0 seconds, 0 milliseconds
      const msUntilNextHour = nextHour.getTime() - now.getTime();

      // Schedule fetch for the next hour
      timeoutRef.current = setTimeout(() => {
        fetchWeatherForecast();
        scheduleNextFetch(); // Reschedule for the next hour after this fetch
      }, msUntilNextHour);
    };

    // Run immediately
    restoreWeatherForecast();
    scheduleNextFetch();

    return () => {
      // Clear timeout if exists
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Abort any pending fetch
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [queryKey]);

  return forecast;
};
